import React, { useState, useEffect } from 'react';
import Navigation from '../components/Navigation';
import { supabase } from '../lib/supabase';
import { Plus, Edit, Trash2, FileText, Loader2 } from 'lucide-react';
import ReportGenerator from '../components/ReportGenerator';
import CustomReportModal from '../components/CustomReportModal';
import { toast } from '../lib/toast';

interface ReportFormat {
  id: string;
  name: string;
  settings: {
    companyLogo: boolean;
    companyName: boolean;
    reportType: boolean;
    preparedBy: boolean;
    generatedOn: boolean;
    projectSnapshot: boolean;
    projectHighlights: boolean;
    tasksSnapshot: boolean;
    tasksHighlights: boolean;
    teamProductivity: boolean;
    additionalInfo: boolean;
  };
  created_at: string;
}

export default function Reports({ navigate }: { navigate: (to: string) => void }) {
  const [loading, setLoading] = useState(true);
  const [reportFormats, setReportFormats] = useState<ReportFormat[]>([]);
  const [showGenerator, setShowGenerator] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  useEffect(() => {
    fetchReportFormats();
  }, []);

  const fetchReportFormats = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('custom_report_formats')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setReportFormats(data || []);
    } catch (error) {
      console.error('Error fetching report formats:', error);
      toast.error('Failed to load report formats');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteFormat = async (id: string) => {
    if (!confirm('Are you sure you want to delete this report format?')) {
      return;
    }

    try {
      setDeletingId(id);
      const { error } = await supabase
        .from('custom_report_formats')
        .delete()
        .eq('id', id);

      if (error) throw error;
      
      setReportFormats(reportFormats.filter(format => format.id !== id));
      toast.success('Report format deleted successfully');
    } catch (error) {
      console.error('Error deleting report format:', error);
      toast.error('Failed to delete report format');
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <Navigation
        onSignOut={async () => await supabase.auth.signOut()}
        tasks={[]}
        currentView="all"
        onViewChange={() => {}}
        navigate={navigate}
      />

      <main className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
        <div className="px-4 sm:px-0">
          <div className="flex justify-between items-center mb-6">
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Reports</h1>
            <div className="flex space-x-3">
              <button
                onClick={() => setShowGenerator(true)}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
              >
                <FileText className="h-5 w-5 mr-2" />
                Generate Report
              </button>
              <button
                onClick={() => navigate('/reports/create')}
                className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md shadow-sm text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
              >
                <Plus className="h-5 w-5 mr-2" />
                Create Format
              </button>
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800 shadow sm:rounded-lg overflow-hidden">
            <div className="px-4 py-5 sm:p-6">
              <h2 className="text-lg font-medium text-gray-900 dark:text-white mb-4">Saved Report Formats</h2>
              
              {loading ? (
                <div className="flex justify-center py-8">
                  <Loader2 className="h-8 w-8 animate-spin text-indigo-600" />
                </div>
              ) : reportFormats.length === 0 ? (
                <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-8 text-center">
                  <p className="text-gray-500 dark:text-gray-400 mb-4">
                    You haven't created any custom report formats yet.
                  </p>
                  <p className="text-gray-500 dark:text-gray-400 mb-6">
                    Custom formats allow you to define which sections appear in your reports.
                  </p>
                </div>
              ) : (
                <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
                  {reportFormats.map((format) => (
                    <div
                      key={format.id}
                      className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4 relative"
                    >
                      <div className="flex justify-between items-start mb-2">
                        <h3 className="text-md font-medium text-gray-900 dark:text-white">
                          {format.name}
                        </h3>
                        <div className="flex space-x-2">
                          <button
                            onClick={() => navigate(`/reports/edit/${format.id}`)}
                            className="text-gray-400 hover:text-indigo-500 dark:hover:text-indigo-400"
                            title="Edit format"
                          >
                            <Edit className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => handleDeleteFormat(format.id)}
                            disabled={deletingId === format.id}
                            className="text-gray-400 hover:text-red-500 dark:hover:text-red-400"
                            title="Delete format"
                          >
                            {deletingId === format.id ? (
                              <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                              <Trash2 className="h-4 w-4" />
                            )}
                          </button>
                        </div>
                      </div>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">
                        Created on {new Date(format.created_at).toLocaleDateString()}
                      </p>
                      <div className="space-y-1">
                        {Object.entries(format.settings).map(([key, value]) => (
                          value && (
                            <div key={key} className="flex items-center">
                              <div className="w-2 h-2 bg-indigo-500 rounded-full mr-2"></div>
                              <span className="text-sm text-gray-600 dark:text-gray-300">
                                {key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}
                              </span>
                            </div>
                          )
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="mt-8 bg-white dark:bg-gray-800 shadow sm:rounded-lg overflow-hidden">
            <div className="px-4 py-5 sm:p-6">
              <h2 className="text-lg font-medium text-gray-900 dark:text-white mb-4">About Reports</h2>
              <div className="prose prose-sm dark:prose-invert max-w-none">
                <p>
                  Generate professional reports for your clients and team members. You can create custom report formats
                  to include exactly the information you need.
                </p>
                <h3>Report Features:</h3>
                <ul>
                  <li><strong>Custom Formats:</strong> Create and save report templates with your preferred sections</li>
                  <li><strong>Project Snapshots:</strong> Include project status, progress, and key metrics</li>
                  <li><strong>Task Highlights:</strong> Summarize completed and upcoming tasks</li>
                  <li><strong>Team Productivity:</strong> Track team performance and contributions</li>
                  <li><strong>Multiple Output Formats:</strong> Generate reports as PDF or text files</li>
                </ul>
                <p>
                  To get started, click "Create Format" to define your report structure, then use "Generate Report" to create
                  reports based on your saved formats.
                </p>
              </div>
            </div>
          </div>
        </div>
      </main>

      {showGenerator && (
        <ReportGenerator onClose={() => setShowGenerator(false)} />
      )}

      {showCreateModal && (
        <CustomReportModal 
          onClose={() => setShowCreateModal(false)} 
          onSuccess={() => {
            setShowCreateModal(false);
            fetchReportFormats();
          }}
        />
      )}
    </div>
  );
}